import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { assignSpareToBike } from "@/integrations/supabase/inventory-services";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";

import { BranchInventoryWithRelations } from "@/types/inventory";

interface AssignToBikeModalProps {
  isOpen: boolean;
  onClose: () => void;
  inventoryItem: BranchInventoryWithRelations | null;
  onSuccess: () => void;
}

const AssignToBikeModal = ({ isOpen, onClose, inventoryItem, onSuccess }: AssignToBikeModalProps) => {
  const [bikeId, setBikeId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [notes, setNotes] = useState("");
  const { profile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: bikes } = useQuery({
    queryKey: ['bikes', profile?.branch_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bikes')
        .select('*')
        .eq('branch_id', profile?.branch_id)
        .eq('active', true)
        .order('number');
      if (error) throw error;
      return data;
    },
    enabled: !!profile?.branch_id,
  });

  const assignMutation = useMutation({
    mutationFn: ({ bikeId, sparePartTypeId, branchId, quantity, notes }: {
      bikeId: string;
      sparePartTypeId: string;
      branchId: string;
      quantity: number;
      notes?: string;
    }) => assignSpareToBike(bikeId, sparePartTypeId, branchId, quantity, notes),
    onSuccess: () => {
      toast({
        title: "¡Éxito!",
        description: "Repuesto asignado a la bicicleta correctamente.",
      });
      queryClient.invalidateQueries({ queryKey: ['branch-inventory'] });
      queryClient.invalidateQueries({ queryKey: ['bike-spare-assignments'] });
      onSuccess();
      handleClose();
    },
    onError: (error: unknown) => {
      console.error("Error assigning spare to bike:", error);
      const errorMessage = error instanceof Error ? error.message : "Error al asignar el repuesto.";
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!bikeId || !quantity || !inventoryItem || !profile?.branch_id) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Por favor completa todos los campos requeridos.",
      });
      return;
    }

    const quantityNum = parseInt(quantity);
    if (quantityNum <= 0 || quantityNum > inventoryItem.quantity) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "La cantidad debe ser mayor a 0 y menor o igual al stock disponible.",
      });
      return;
    }

    assignMutation.mutate({
      bikeId,
      sparePartTypeId: inventoryItem.spare_part_type_id,
      branchId: profile.branch_id,
      quantity: quantityNum,
      notes: notes.trim() || undefined
    });
  };

  const handleClose = () => {
    setBikeId("");
    setQuantity("");
    setNotes("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Asignar Repuesto a Bicicleta</DialogTitle>
        </DialogHeader>

        {inventoryItem && (
          <div className="mb-4 p-3 bg-gray-50 rounded-md">
            <p className="font-medium">{inventoryItem.spare_part_type?.name}</p>
            <p className="text-sm text-gray-600">
              Stock disponible: {inventoryItem.quantity} unidades
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bike">Bicicleta</Label>
            <Select value={bikeId} onValueChange={setBikeId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecciona una bicicleta" />
              </SelectTrigger>
              <SelectContent>
                {bikes?.map((bike) => (
                  <SelectItem key={bike.id} value={bike.id}>
                    #{bike.number} - {bike.model} ({bike.color})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="quantity">Cantidad Utilizada</Label>
            <Input
              id="quantity"
              type="number"
              min="1"
              max={inventoryItem?.quantity || 0}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="Cantidad"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notas del Trabajo (Opcional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Descripción del trabajo realizado..."
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={assignMutation.isPending}>
              {assignMutation.isPending ? "Asignando..." : "Asignar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AssignToBikeModal;
